import React from 'react';
import moment from 'moment';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import Chip from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Chip';
import useResource from 'Hooks/useResource';
import { fullDate } from 'Consts/DateFormats';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import { getLinkQueryParamUrl } from 'Helpers/Query';
import {
  applyFilters, filtersToKeep,
  hiddenBulkCommunicationFilters,
} from 'Components/Modals/BulkCommunicationDialog/BulkCommunicationFilterActions';
import Button from 'Components/UI/Button';
import Typography from 'Components/UI/Typography';
import Paper from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Paper';
import { isEmpty } from 'lodash';
import ReplayIcon from '@material-ui/icons/Replay';
import { useBulkCommunicationFilterStateContext, useBulkCommunicationFilterDispatchContext } from 'Components/Modals/BulkCommunicationDialog/BulkCommunicationFilterContext';

const styles = (theme) => ({
  root: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  filters: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
  },
  filterChip: {
    margin: theme.spacing(1, 1, 0, 0),
  },
  filterChipRoot: {
    backgroundColor: theme.palette.facet.accentBlue2,
    color: theme.palette.common.white,
    fontWeight: 'bold',
  },
  filtersHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  reset: {
    marginLeft: theme.spacing(2),
  },
});

const BulkCommunicationSelectedFilters = ({
  classes,
  className,
  resetSelectedFilterGroups,
  linkUrl,
  onApplyFilters,
  ...rest
}) => {
  const { pendingFilters, appliedFilters } = useBulkCommunicationFilterStateContext();

  const filterDispatch = useBulkCommunicationFilterDispatchContext();
  const { resource: resourceFromContext } = useMetaLayoutContext();
  const { resource } = useResource(resourceFromContext);
  const { logEvent } = useEventTrackingLogEvent();

  const handleOnDelete = (filterKey) => {
    filterDispatch({ type: 'removeFilter', name: filterKey });
    const updatedFilterState = appliedFilters;
    delete updatedFilterState[filterKey];
    applyFilters(updatedFilterState, resource, linkUrl, onApplyFilters);
    logEvent('Bulk Communication: Clicked Remove Filter', { filterKey });
  };

  const handleResetFilters = () => {
    logEvent('Bulk Communication: Clicked Reset Filters Button');

    // We don't have a way to mark certain query parameters as required
    const url = getLinkQueryParamUrl(resource, linkUrl);

    const filters = pendingFilters;
    url.searchParams.forEach((value, key) => {
      if (!filtersToKeep.includes(key)) {
        filterDispatch({ type: 'removeFilter', name: key });
        delete filters[key];
      }
    });
    applyFilters(filters, resource, linkUrl, onApplyFilters);
    resetSelectedFilterGroups(); // <-- Resets the selected filter groups (handles disabling the apply filter(s) buttons)
  };

  const selectedFilters = Object.keys(appliedFilters).reduce((acc, curr) => {
    /* eslint-disable no-param-reassign */
    if (!hiddenBulkCommunicationFilters.includes(curr)) {
      acc[curr] = appliedFilters[curr];
    }
    return acc;
  }, {});

  const hasSelectedFilters = !isEmpty(selectedFilters);

  return hasSelectedFilters && (
    <Paper
      className={classes.paper}
      elevation={1}
      {...rest}
    >
      <div className={classNames(classes.root, className)}>
        <div className={classes.filtersHeader}>
          <Typography
            variant="h2"
            color="primary"
          >
            {'Selected Filters'}
          </Typography>
          <Button
            className={classes.reset}
            color="accentBlue"
            onClick={handleResetFilters}
            startIcon={<ReplayIcon />}
          >
            {'Reset Filters'}
          </Button>
        </div>
        <div className={classes.filters}>
          {Object.keys(selectedFilters).map(filterKey => {
            const filter = resource?.meta().queries().head().template()
              .list()
              .find(fltr => fltr.attr('name') === filterKey);
            const filterPrompt = filter.attr('prompt');
            let filterValue = appliedFilters[filterKey];
            if (filter.attr('type') === 'date' || filter.attr('type') === 'datetime') {
              filterValue = moment(filterValue).format(fullDate);
            }
            return filterPrompt && (
              <Chip
                id={`bulk-communication-selected-filter-${filterKey}`}
                test={'bulk-communication-selected-filter'}
                key={filterKey}
                className={classes.filterChip}
                classes={{
                  root: classes.filterChipRoot,
                }}
                label={`${filterPrompt} - ${filterValue}`}
                onDelete={() => handleOnDelete(filterKey)}
              />
            );
          })}
        </div>
      </div>
    </Paper>
  );
};

BulkCommunicationSelectedFilters.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  linkUrl: PropTypes.string,
  onApplyFilters: PropTypes.func,
};

BulkCommunicationSelectedFilters.defaultProps = {
  className: '',
  linkUrl: 'bulkCommunicationLink',
  onApplyFilters: null,
};

export default withStyles(styles)(BulkCommunicationSelectedFilters);
